import type { OverviewSnapshot } from '@kenocyber/game-domain';
import {
  type ActivePetWire,
  type ArtifactsResponse,
  type EconomyMeResponse,
  findChassisFinish,
  findEnergyColor,
} from '@kenocyber/shared';
import { useEffect, useId, useState } from 'react';
import { artifactEffectLines, formatBps, formatScaled } from './artifact-format';
import { formatDecimal, formatNumber, formatPercent, formatVipRemaining } from './format';
import { rarityLabel } from './rarity';

/** Names for the two appearance axes, so the Overview never shows a raw id. */
const energyName = (id: string) => findEnergyColor(id)?.name ?? id;
const finishName = (id: string) => findChassisFinish(id)?.name ?? id;

interface OverviewPanelProps {
  readonly snapshot: OverviewSnapshot;
  /** Null until the artifact state has been read from the server. */
  readonly artifacts: ArtifactsResponse | null;
  /** The pet the session is bound to, for its name, stars and rarity. */
  readonly activePet: ActivePetWire | null;
  /** The SERVER's wallets and VIP state. Null until the first read lands. */
  readonly economy: EconomyMeResponse | null;
  readonly onClose: () => void;
}

const CANNON_RARITY_LABEL: Record<string, string> = {
  excellent: 'Excellent',
  perfect: 'Perfect',
  anomaly: 'Anomaly',
};

function StatRow({
  label,
  base,
  bonus,
  total,
  testId,
}: {
  label: string;
  base: string;
  bonus: string;
  total: string;
  testId?: string;
}) {
  return (
    <div className="stat-row" data-testid={testId}>
      <span className="stat-label">{label}</span>
      <span className="stat-base">{base}</span>
      <span className="stat-bonus">{bonus}</span>
      <span className="stat-total">{total}</span>
    </div>
  );
}

function InfoRow({
  label,
  value,
  testId,
  wide,
}: {
  label: string;
  value: string;
  testId?: string;
  /** Keeps this row across BOTH columns of an `.ov-pairs` grid. */
  wide?: boolean;
}) {
  return (
    <div className="info-row" data-wide={wide ? 'true' : undefined}>
      <span className="stat-label">{label}</span>
      <span className="info-value" data-testid={testId}>
        {value}
      </span>
    </div>
  );
}

/** One figure of the artifact summary. Two of these fit per row. */
function TotalCell({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <div className="ov-total">
      <span className="stat-label">{label}</span>
      <span className="info-value" data-testid={testId}>
        {value}
      </span>
    </div>
  );
}

/**
 * Floating analytical panel, anchored under the Overview button.
 *
 * It is absolutely positioned so opening it never reserves layout space nor
 * resizes the arena - the Phaser canvas is untouched. Deliberately does NOT
 * repeat the health bar, XP bar or point allocation, which live in the HUD.
 */
export function OverviewPanel({
  snapshot,
  artifacts,
  activePet,
  economy,
  onClose,
}: OverviewPanelProps) {
  // Collapsed by default: the summary is what an overview is for, and the
  // per-artifact breakdown is a detour a player takes deliberately.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const petDetailsId = useId();
  // Collapsed by default, like the artifact breakdown: an overview that needs
  // a scrollbar to reach Armamento is not an overview.
  const [petOpen, setPetOpen] = useState(false);
  const detailsId = useId();
  const ownedArtifacts = (artifacts?.artifacts ?? []).filter(
    (artifact) => artifact.copies.length > 0,
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <aside className="overview-panel" data-testid="overview-panel" aria-label="Overview">
      <header className="overview-panel-header">
        <h2>Overview</h2>
        <button
          type="button"
          className="overview-close"
          data-testid="overview-close"
          aria-label="Fechar Overview"
          onClick={onClose}
        >
          ×
        </button>
      </header>

      <div className="overview-panel-body">
        <section>
          <h3>Atributos</h3>
          <div className="stat-header">
            <span />
            <span>Base</span>
            <span>Bônus</span>
            <span>Total</span>
          </div>
          <StatRow
            label="Dano"
            base={formatNumber(snapshot.baseDamage)}
            bonus={formatPercent(snapshot.damageBonusPercent)}
            total={formatNumber(snapshot.totalDamagePerProjectile)}
            testId="ov-damage"
          />
          {/*
            The bonus attack, on its OWN line.
            It is not inside the base and it is not inside the percentage: the
            arena adds it to each projectile after the main formula, and folding
            it into either column would print a formula the game does not use.
            Hidden when it is zero, which is every account without a component.
          */}
          {snapshot.bonusAttackPerProjectile > 0 ? (
            <StatRow
              label="Ataque bônus"
              base="—"
              bonus="—"
              total={`+${formatNumber(snapshot.bonusAttackPerProjectile)}`}
              testId="ov-bonus-attack"
            />
          ) : null}
          <StatRow
            label="Vel. ataque"
            base={formatNumber(snapshot.baseAttacksPerSecond)}
            bonus={formatPercent(snapshot.attackSpeedBonusPercent)}
            total={`${formatNumber(snapshot.totalAttacksPerSecond)}/s`}
            testId="ov-attack-speed"
          />
          {/*
            **A cadência que a caça EMITE, ao lado da que a conta pede** (gate
            V28I.CF.3).

            A linha acima continua a dizer, inteiro, o que o equipamento
            concede: nada é dividido nem escondido. Esta diz quantas salvas a
            caça admite por segundo, e quando o teto morde di-lo por PALAVRAS —
            nunca só por cor — para a diferença ser legível em vez de
            misteriosa. Sem teto a morder, as duas linhas dizem o mesmo número.
          */}
          <StatRow
            label="Cadência efetiva"
            base="—"
            bonus={snapshot.cadenceLimited ? 'LIMITADA PELA CAÇA' : '—'}
            total={`${formatNumber(snapshot.emittedAttacksPerSecond)}/s`}
            testId="ov-emitted-cadence"
          />
          <StatRow
            label="Vida"
            base={formatNumber(snapshot.baseHealth, 0)}
            bonus={formatPercent(snapshot.healthBonusPercent)}
            total={formatNumber(snapshot.maxHealth, 0)}
            testId="ov-max-health"
          />
        </section>

        <section>
          <h3>Armamento</h3>
          <div className="ov-pairs">
            <InfoRow
              label="Raridade do canhão"
              value={CANNON_RARITY_LABEL[snapshot.cannonRarity] ?? snapshot.cannonRarity}
              testId="ov-cannon-rarity"
              wide
            />
            <InfoRow
              label="Nível do canhão"
              value={String(snapshot.cannonLevel)}
              testId="ov-cannon-level"
            />
            <InfoRow
              label="Canhões"
              value={String(snapshot.cannonCount)}
              testId="ov-cannon-count"
            />
            <InfoRow
              label="Projéteis por canhão"
              value={String(snapshot.projectilesPerCannon)}
              testId="ov-projectiles-per-cannon"
            />
            <InfoRow
              label="Projéteis por disparo"
              value={String(snapshot.projectilesPerVolley)}
              testId="ov-projectiles-per-volley"
            />
          </div>
        </section>

        {/*
          The active pet, compact.

          Combat numbers come from the SIMULATION's own snapshot, never
          recomputed here, so the panel can never disagree with the pet that
          is flying. `Dano na sessão` is a SHARE of the session damage above
          rather than an addition to it - the pet's hits went through the same
          damage path and were already counted once.
        */}
        {snapshot.pet && activePet ? (
          <section data-testid="ov-pet">
            <div className="ov-artifacts-head">
              <h3>
                Pet
                <span className="ov-artifacts-count" data-testid="ov-pet-name">
                  {activePet.name}
                </span>
              </h3>
              <button
                type="button"
                className="ov-artifacts-toggle"
                data-testid="ov-pet-toggle"
                aria-expanded={petOpen}
                aria-controls={petDetailsId}
                onClick={() => setPetOpen((open) => !open)}
              >
                {petOpen ? 'Ocultar detalhes' : 'Ver detalhes'}
              </button>
            </div>
            <div className="ov-totals">
              <TotalCell
                label="Raridade"
                value={`${rarityLabel(activePet.rarity)} ${'★'.repeat(activePet.stars)}${'☆'.repeat(
                  activePet.starSlots - activePet.stars,
                )}`}
                testId="ov-pet-rarity"
              />
              <TotalCell
                label="Evolução"
                value={`${activePet.formName} (${snapshot.pet.evolution})`}
                testId="ov-pet-evolution"
              />
              <TotalCell
                label="Dano na sessão"
                value={formatNumber(snapshot.pet.damageDealt, 0)}
                testId="ov-pet-damage-dealt"
              />
            </div>
            {petOpen ? (
              <div className="ov-pet-details" id={petDetailsId} data-testid="ov-pet-details">
                <div className="ov-totals">
                  <TotalCell
                    label="Metralhadoras"
                    value={String(snapshot.pet.muzzles)}
                    testId="ov-pet-muzzles"
                  />
                  <TotalCell
                    label="Dano por projétil"
                    value={formatNumber(snapshot.pet.damagePerBullet)}
                    testId="ov-pet-damage-per-bullet"
                  />
                  <TotalCell
                    label="Cadência"
                    value={`${formatNumber(snapshot.pet.bulletsPerSecond)}/s`}
                    testId="ov-pet-cadence"
                  />
                  <TotalCell
                    label="DPS estimado"
                    value={formatNumber(snapshot.pet.damagePerSecond)}
                    testId="ov-pet-dps"
                  />
                  <TotalCell
                    label="Treino de dano"
                    value={`${activePet.training.damageFoodCount} (+${(
                      activePet.training.damageBonusBps / 100
                    ).toFixed(1)}%)`}
                    testId="ov-pet-damage-training"
                  />
                  <TotalCell
                    label="Treino de cadência"
                    value={`${activePet.training.attackSpeedFoodCount} (+${(
                      activePet.training.attackSpeedBonusBps / 100
                    ).toFixed(1)}%)`}
                    testId="ov-pet-speed-training"
                  />
                  <TotalCell
                    label="Cor dos efeitos"
                    value={energyName(activePet.energyId)}
                    testId="ov-pet-energy"
                  />
                  <TotalCell
                    label="Acabamento"
                    value={finishName(activePet.finishId)}
                    testId="ov-pet-finish"
                  />
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

        {/*
          Artifact passives, compact.

          The main view shows ONLY the summed contribution - eight figures in
          two columns. Listing all ten artifacts inline made this panel tall
          enough to need a scrollbar and pushed Armamento and Sessão below the
          fold, which is the opposite of what an overview is for.

          The individual breakdown is one click away and starts collapsed. It
          opens into its own bounded, scrollable region, so it can never push
          the attributes off the screen.

          Deliberately does NOT repeat the health or XP bars - those live in
          the HUD - and opening this panel never interrupts the hunt.
        */}
        {artifacts ? (
          <section data-testid="ov-artifacts">
            <div className="ov-artifacts-head">
              <h3>
                Passivas de artefatos
                <span className="ov-artifacts-count" data-testid="ov-artifacts-count">
                  {ownedArtifacts.length} {ownedArtifacts.length === 1 ? 'ativa' : 'ativas'}
                </span>
              </h3>
              <button
                type="button"
                className="ov-artifacts-toggle"
                data-testid="ov-artifacts-toggle"
                aria-expanded={detailsOpen}
                aria-controls={detailsId}
                onClick={() => setDetailsOpen((open) => !open)}
              >
                {detailsOpen ? 'Ocultar detalhes' : 'Ver detalhes'}
              </button>
            </div>

            {/* Two columns: the whole summary fits without a scrollbar at
                1366x768, which the inline list did not. */}
            <div className="ov-totals">
              <TotalCell
                label="Dano"
                value={`+${formatBps(artifacts.totals.damageBps)}`}
                testId="ov-artifact-total-damage"
              />
              <TotalCell
                label="Vida base"
                value={`+${formatScaled(artifacts.totals.baseHealth, 0)}`}
                testId="ov-artifact-total-base-health"
              />
              <TotalCell
                label="Vida máxima"
                value={`+${formatBps(artifacts.totals.maxHealthBps)}`}
                testId="ov-artifact-total-max-health"
              />
              <TotalCell
                label="ASPD base"
                value={`+${formatScaled(artifacts.totals.baseAttackSpeedMicro, 6)}/s`}
                testId="ov-artifact-total-attack-speed"
              />
              <TotalCell
                label="Ouro fixo"
                value={`+${formatScaled(artifacts.totals.goldPerKill, 0)}`}
                testId="ov-artifact-total-gold-flat"
              />
              <TotalCell
                label="Ouro %"
                value={`+${formatBps(artifacts.totals.goldBps)}`}
                testId="ov-artifact-total-gold-percent"
              />
              <TotalCell
                label="Recuperação"
                value={`+${formatScaled(artifacts.totals.regenMilliPerSecond, 3)}/s`}
                testId="ov-artifact-total-regen"
              />
              <TotalCell
                label="Baby Alien"
                value={`+${formatScaled(artifacts.totals.babyAlienChanceMicroPp, 6)} p.p.`}
                testId="ov-artifact-total-baby-alien"
              />
            </div>

            {artifacts.totals.babyAlienChanceMicroPp === '0' ? null : (
              <p className="ov-note" data-testid="ov-baby-alien-note">
                O sistema de Baby Aliens ainda não foi lançado.
              </p>
            )}

            {/* Collapsed by default. Bounded height plus its own scroll, so
                opening it never grows the panel past the viewport. */}
            {detailsOpen ? (
              <div className="ov-artifact-list" id={detailsId} data-testid="ov-artifact-list">
                {artifacts.artifacts.map((artifact) => {
                  const owned = artifact.copies.length > 0;
                  const lines = owned ? artifactEffectLines(artifact.effects) : [];
                  return (
                    <div
                      key={artifact.definitionId}
                      className="ov-artifact"
                      data-owned={owned ? 'true' : 'false'}
                      data-testid={`ov-artifact-${artifact.definitionId}`}
                    >
                      <span className="stat-label">
                        {artifact.name}
                        <em> · {rarityLabel(artifact.rarity)}</em>
                      </span>
                      <span
                        className="info-value"
                        data-testid={`ov-artifact-level-${artifact.definitionId}`}
                      >
                        {owned ? `NV.${artifact.effectiveLevel}` : 'não obtido'}
                      </span>
                      {/* Each artifact's own contribution, separately: the
                          totals above are a sum, and a player has to be able
                          to see what each one puts into it. */}
                      <span className="ov-artifact-effects">
                        {lines.length === 0 ? '—' : lines.map((line) => line.value).join(' · ')}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </section>
        ) : null}

        {/* Server-derived, never predicted. The panel states what VIP is worth
            whether or not it is active, so the effect is legible before anyone
            pays for it - and the numbers come from the shared constants that
            drive the multipliers themselves, never typed a second time. */}
        {economy ? (
          <section data-testid="ov-vip">
            <h3>VIP</h3>
            <div className="ov-pairs">
              <InfoRow
                label="Estado"
                value={
                  economy.vip.active
                    ? `Ativo — ${formatVipRemaining(economy.vip.remainingSeconds)} restante(s)`
                    : 'Inativo'
                }
                testId="ov-vip-state"
                wide
              />
              <InfoRow
                label="Bônus de XP"
                value={`+${economy.vip.xpBonusPercent}%`}
                testId="ov-vip-xp"
              />
              <InfoRow
                label="Bônus de Ouro"
                value={`+${economy.vip.goldBonusPercent}%`}
                testId="ov-vip-gold"
              />
              <InfoRow
                label="Trade direto"
                value={economy.vip.directTradeAllowed ? 'Liberado' : 'Exclusivo VIP'}
                testId="ov-vip-market"
              />
              <InfoRow label="Cash" value={formatDecimal(economy.cash)} testId="ov-cash" />
            </div>
          </section>
        ) : null}

        <section>
          <h3>Sessão</h3>
          <div className="ov-pairs">
            <InfoRow
              label="Dano total"
              value={formatNumber(snapshot.damageMetric.sessionTotal)}
              testId="ov-session-damage"
            />
            <InfoRow
              label="Ouro total"
              value={formatNumber(snapshot.goldMetric.sessionTotal, 0)}
              testId="ov-session-gold"
            />
            <InfoRow label="Mobs em campo" value={String(snapshot.mobsOnField)} testId="ov-mobs" />
            <InfoRow label="Abates" value={String(snapshot.kills)} testId="ov-kills" />
            <InfoRow label="Mapa" value={String(snapshot.mapIndex)} testId="ov-map" />
          </div>
        </section>
      </div>
    </aside>
  );
}
